import SSOModule from '@abaco/sso-web-module/src/abaco-module';
import SsoVuexModule from '@abaco/sso-web-module/src/abaco-module/state';
import { AbacoWebModule, ApplicationReadyEvent, MenuItem } from '@abaco/web-common/src/app';
import { SubMenuItem } from '@abaco/web-common/src/app/Application';
import { BaseUser } from '@abaco/web-common/src/BaseUser';
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import globals from "./globals";
import { MenuHierarchy } from './models/AllowedMenu';
import MenuVuexModule from "./state";

export const MODULE_ID = "menu";

export interface MenuItemWithOrd extends MenuItem {
    ord?: number;
    enabled?: number;
    children?: SubMenuItemWithOrd[];
}

export interface SubMenuItemWithOrd extends SubMenuItem {
    enabled?: number;
    ord?: number;
}

export type LoadMenuApi = (http: HTTPClient) => Promise<MenuHierarchy[]>;

export default class MenuModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;

    readonly id = MODULE_ID;

    readonly globalComponents = globals;
    readonly vuexModule = new MenuVuexModule();
    readonly notifyAnyRoutes = true;

    private http!: HTTPClient;
    private ssoVuexModule!: SsoVuexModule;
    private allMenu!: MenuItem[];
    private allowedMenuPromise: Promise<MenuHierarchy[]> | null = null;
    private homePageId!: string;

    private loadMenuApi!: LoadMenuApi | undefined;

    constructor(loadMenuApi?: LoadMenuApi) {
        this.loadMenuApi = loadMenuApi
    }

    onApplicationReady(event: ApplicationReadyEvent) {
        this.http = event.http;
        this.homePageId = event.envConfig["home-page"];
        const ssoModule = event.modules.find((m) => m.id === SSOModule.MODULE_ID) as SSOModule;
        if (ssoModule) {
            this.ssoVuexModule = ssoModule.vuexModule;
            this.allowedMenuPromise = this.getHierarchyMenu(ssoModule.vuexModule.user);
        } else {
            this.allowedMenuPromise = Promise.resolve([]);
        }

        let menu: MenuItem[] = [];
        for (const m of event.modules) {
            if (m.menu) {
                menu.push(...m.menu);
            }
        }
        this.allMenu = menu;

        this.allowedMenuPromise.then(async (allowedMenu) => {
            if (allowedMenu) {
                menu = this.mapMenu(allowedMenu, menu);
            }

            if(process.env.VUE_APP_MODE === "malta-pmp" && menu.length > 0){
                menu = [menu[0]];
            }

            this.vuexModule.homePage = this.getHomePageMenuItem(menu);
            this.vuexModule.menu = menu;
            this.vuexModule.loading = false;
            this.allowedMenuPromise = null;
        });
    }

    private mapMenu(allowedMenu: MenuHierarchy[], menu: MenuItem[]) {
        const menuMap = this.toFlatMenu(menu);
        const resMenu: MenuItemWithOrd[] = [];

        for (const m of allowedMenu) {
            const realMenu = menuMap[m.id];
            if (m.items && m.items.length) {
                const children = this.computeChildren(m.items, menuMap);
                if (children.length) {
                    resMenu.push({
                        descrKey: m.id,
                        id: m.id,
                        icon: "",
                        isVisible: true,
                        enabled: 1,
                        onClick: () => { return null },
                        isOpen: true,
                        children,
                        ord: m.ord
                    })
                }
            } else if (realMenu && (!realMenu.children || realMenu.children.length == 0) && (!m.items || m.items.length == 0)) {
                resMenu.push({ ...menuMap[m.id], ord: m.ord, enabled: m.enabled });
            }
        }

        return resMenu;
    }

    private computeChildren(children: MenuHierarchy[], menuMap: Record<string, MenuItem>) {
        const res: MenuItemWithOrd[] = [];
        let curChild;
        children.forEach(c => {
            curChild = menuMap[c.id];
            if (curChild) {
                res.push({ ...curChild, ord: c.ord, enabled: c.enabled });
            }
        })
        return res;
    }

    private toFlatMenu(menu: MenuItem[]) {
        let obj: Record<string, MenuItem> = {};
        menu.forEach(m => {
            if (m.children && m.children.length) {
                obj = { ...obj, ...this.toFlatMenu(m.children) };
            }
            obj[m.id] = m;
        })

        return obj;
    }

    async beforeEnterAnyRoutes() {
        if (!this.vuexModule.menu || this.vuexModule.menu.length === 0) {
            await this.loadMenu()
        }
    }

    public async loadMenu() {
        if (this.allowedMenuPromise != null) {
            return this.allowedMenuPromise;
        }
        if (!this.ssoVuexModule) {
            return;
        }
        this.vuexModule.loading = true;

        this.allowedMenuPromise = this.getHierarchyMenu(this.ssoVuexModule.user)
        const allowedMenu = await this.allowedMenuPromise;
        this.allowedMenuPromise = null;

        let menu: MenuItem[] = this.allMenu;
        if (allowedMenu) {
            menu = this.mapMenu(allowedMenu, menu);
        }

        if(process.env.VUE_APP_MODE === "malta-pmp" && menu.length > 0){
            menu = [menu[0]];
        }

        this.vuexModule.homePage = this.getHomePageMenuItem(menu)
        this.vuexModule.menu = menu;
        this.vuexModule.loading = false;
    }

    public setMenuItem(val: MenuItem | null) {
        this.vuexModule.curMenuItem = val;
    }

    public navigateToMenu(val: MenuItem){
        this.setMenuItem(val);
        val.onClick();
    }

    public async getHierarchyMenu(user: BaseUser | null): Promise<MenuHierarchy[]> {
        if (!user) {
            return [];
        }
        if (this.loadMenuApi) {
            return this.loadMenuApi(this.http);
        }
        const res = await this.http.get("/sitiagri-rest-api/api_basic/v1/user_registry/hierarchy_menu", { params: { sort: "ord" } });
        return res.data;
    }

    private getHomePageMenuItem(menu: MenuItemWithOrd[]): MenuItem | null {
        if (!this.homePageId) {
            return menu && menu.length ? menu[0] : null;
        }
        return this.getMenuItemFromMenuId(this.homePageId, menu) || null;
    }

    private getMenuItemFromMenuId(menuItemId: string, menu: MenuItem[]): MenuItem | null {
        let res: MenuItem | null = null;
        for (let index = 0; index < menu.length; index++) {
            const element = menu[index];
            res = menuItemId === element.id ? element : null;
            if (!res && element.children && element.children.length) {
                res = this.getMenuItemFromMenuId(menuItemId, element.children);
            }
            if (res) {
                break;
            }
        }
        return res;
    }
}
